import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Select } from '@packages/ui/composites/Select';
import type { SelectOption } from '@packages/ui/composites/Select';
import type { FilterDef } from '../../schema/types';
import type { DynamicTableV2Props } from '../../types/DynamicTableV2.types';
import { TABLE_IDS } from '../../semantic-contract';

const _filterOptionsCache = new Map<string, Array<{ label: string; value: string | number }>>();

export function getFilterOptionsCache() {
  return _filterOptionsCache;
}

export interface AsyncFilterSelectProps {
  filter: FilterDef;
  value: unknown;
  onFilterChange: (name: string, value: unknown) => void;
  axiosInstance?: DynamicTableV2Props['axiosInstance'];
  disabled?: boolean;
  onOptionsLoaded?: (
    name: string,
    options: Array<{ label: string; value: string | number }>
  ) => void;
  staticOptions?: Array<{ label: string; value: string | number | boolean }>;
}

function toSelectValue(
  value: unknown,
  options: SelectOption[],
  isMulti: boolean | undefined,
): SelectOption | SelectOption[] | null {
  if (value === undefined || value === null || value === '') return isMulti ? [] : null;
  if (isMulti && Array.isArray(value)) {
    return value
      .map((v) => options.find((o) => String(o.value) === String(v)))
      .filter(Boolean) as SelectOption[];
  }
  return options.find((o) => String(o.value) === String(value)) ?? null;
}

export function AsyncFilterSelect({
  filter,
  value,
  onFilterChange,
  axiosInstance,
  disabled = false,
  onOptionsLoaded,
  staticOptions,
}: AsyncFilterSelectProps): JSX.Element {
  const ds = filter.dataSource;
  const isAsync = ds != null;
  const url = isAsync ? ds.url.replace(/^\//, '') : '';
  const cacheKey = url;

  const [options, setOptions] = useState<
    Array<{ label: string; value: string | number }> | undefined
  >(() => {
    if (!isAsync)
      return staticOptions as Array<{ label: string; value: string | number }> | undefined;
    return _filterOptionsCache.get(cacheKey);
  });
  const [isLoading, setIsLoading] = useState(isAsync && !_filterOptionsCache.has(cacheKey));

  const onOptionsLoadedRef = useRef(onOptionsLoaded);
  onOptionsLoadedRef.current = onOptionsLoaded;

  useEffect(() => {
    if (!isAsync) return;
    if (_filterOptionsCache.has(cacheKey)) {
      const cached = _filterOptionsCache.get(cacheKey)!;
      setOptions(cached);
      setIsLoading(false);
      if (cached.length > 0) onOptionsLoadedRef.current?.(filter.name, cached);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const doFetch = async () => {
      try {
        let raw: Record<string, unknown>[];
        if (axiosInstance) {
          const resp = await axiosInstance.get(url);
          raw = Array.isArray(resp.data) ? resp.data : resp.data?.items ?? [];
        } else {
          const token = localStorage.getItem('access_token');
          const base = process.env.REACT_APP_WEALTHLANE_API_ENDPOINT ?? '';
          const fullUrl = ds!.url.startsWith('http') ? ds!.url : `${base}/${url}`;
          const resp = await fetch(fullUrl, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          const json = await resp.json();
          raw = Array.isArray(json) ? json : json?.items ?? [];
        }
        if (cancelled) return;
        const mapped = raw.map((item) => ({
          label: String(item[ds!.labelField] ?? ''),
          value: item[ds!.valueField] as string | number,
        }));
        _filterOptionsCache.set(cacheKey, mapped);
        setOptions(mapped);
        setIsLoading(false);
        if (mapped.length > 0) onOptionsLoadedRef.current?.(filter.name, mapped);
      } catch {
        if (!cancelled) setIsLoading(false);
      }
    };

    void doFetch();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  const selectOptions: SelectOption[] = useMemo(
    () => (options ?? []).map((o) => ({ label: o.label, value: String(o.value) })),
    [options],
  );

  const currentValue = useMemo(
    () => toSelectValue(value, selectOptions, filter.multiple),
    [value, selectOptions, filter.multiple],
  );

  return (
    <Select
      name={filter.name}
      placeholder={filter.placeholder}
      value={currentValue as any}
      onChange={(selected: any) => {
        if (filter.multiple) {
          const vals = Array.isArray(selected) ? selected.map((s: any) => s.value) : [];
          onFilterChange(filter.name, vals.length > 0 ? vals : undefined);
        } else {
          onFilterChange(filter.name, selected?.value ?? undefined);
        }
      }}
      options={selectOptions}
      isMulti={filter.multiple}
      isDisabled={disabled}
      isLoading={isLoading}
      isClearable
      semanticId={TABLE_IDS.filterSelect(filter.name)}
    />
  );
}
